import { DestroyRef, effect, Inject, Injectable, signal, Signal, untracked } from '@angular/core';
import { WINDOW } from '@common';
import { getColumnMaxHeight } from '@common/utils';
import { isNil } from 'lodash';

@Injectable()
export class ElementMaxHeightService {
  private readonly columnHeight = signal<string>(getColumnMaxHeight());
  private observers: ResizeObserver[] = [];

  private refs = signal<Partial<Record<string, HTMLElement>>>({});
  private manualHeights = signal<Partial<Record<string, number>>>({});
  private padding = 0;

  get height(): Signal<string> {
    return this.columnHeight.asReadonly();
  }

  constructor(
    @Inject(WINDOW) private window: Window,
    private destroyRef: DestroyRef
  ) {
    effect(
      () => {
        const currentRefs = this.refs();

        this.clearObservers();
        this.setupObservers(currentRefs);
        this.updateHeight(currentRefs);
      },
      { allowSignalWrites: true }
    );
  }

  updateConfig(overrides?: { customPadding?: number; manualHeights?: Partial<Record<string, number>> }) {
    this.manualHeights.set(overrides?.manualHeights ?? {});
    this.padding = overrides?.customPadding ?? 0;
  }

  updateRef(key: string, el: HTMLElement | null) {
    const current = { ...this.refs() };
    current[key] = el || undefined;

    this.refs.set(current);
  }

  private setupObservers(refs: Partial<Record<string, HTMLElement>>) {
    Object.entries(refs).forEach(([key, el]) => {
      if (!el) return;

      const observer = new ResizeObserver(() => {
        untracked(() => this.updateHeight(this.refs()));
      });

      observer.observe(el);
      this.observers.push(observer);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  private clearObservers() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];
  }

  private updateHeight(refs: Partial<Record<string, HTMLElement>>) {
    const measuredHeights: Partial<Record<string, number>> = Object.entries(refs).reduce(
      (acc, [key, el]) => {
        const heightKey = this.refToHeightKey(key);
        const height = this.getFullHeight(el);

        if (!isNil(height)) acc[heightKey] = height;

        return acc;
      },
      {} as Partial<Record<string, number>>
    );

    const merged = {
      ...measuredHeights,
      ...this.manualHeights()
    };

    const height = getColumnMaxHeight({
      ...merged,
      customPadding: this.padding
    });

    this.columnHeight.set(height);
  }

  private refToHeightKey(refKey: string): string {
    return refKey.replace(/Ref$/, 'Height');
  }

  private getFullHeight(el: HTMLElement | null): number | undefined {
    if (!el) return undefined;

    const rect = el.getBoundingClientRect();
    const style = this.window.getComputedStyle(el);

    const marginTop = parseFloat(style.marginTop || '0');
    const marginBottom = parseFloat(style.marginBottom || '0');

    return Math.ceil(rect.height + marginTop + marginBottom);
  }
}
